import { createContext, useContext, useRef, useState } from 'react';
import { FsConfirmDialog } from './fsConfirmDialog';

const FsConfirmContext = createContext(null);

/**
 * Promise-based confirm in the FS design language. Drop-in replacement for
 * material-ui-confirm's provider/hook pair: confirm({...}) resolves when the
 * user confirms and rejects when they cancel or dismiss.
 */
export const FsConfirmProvider = ({ children }) => {
	const [options, setOptions] = useState(null);
	const resolverRef = useRef(null);

	const confirm = (opts = {}) =>
		new Promise((resolve, reject) => {
			resolverRef.current = { resolve, reject };
			setOptions(opts);
		});

	const handleClose = (confirmed) => {
		const resolver = resolverRef.current;
		resolverRef.current = null;
		setOptions(null);
		if (!resolver) {
			return;
		}
		if (confirmed) {
			resolver.resolve();
		} else {
			resolver.reject();
		}
	};

	return (
		<FsConfirmContext.Provider value={confirm}>
			{children}
			<FsConfirmDialog
				state={!!options}
				options={options || {}}
				onClose={handleClose}
			/>
		</FsConfirmContext.Provider>
	);
};

export const useFsConfirm = () => {
	const confirm = useContext(FsConfirmContext);
	if (!confirm) {
		throw new Error('useFsConfirm must be used within an FsConfirmProvider');
	}
	return confirm;
};
